<script lang="ts">
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiCheck } from "@mdi/js"
  import LearningDisclosure from "../../_shared/_modules/learning-disclosure"
  import type { GuideCourse } from "../../../../../../../../api/kehrblatt/learning-guide"
  import { spaLink } from "../../../../../../../../_shared/_logic/navigation"
  import {
    getLearningLessonHref,
    getLearningLessonNumber,
    getLearningPathProgress,
  } from "../../../../../../_shared/_logic/learning-path"

  const {
    course,
    basePath,
    activeLessonKey = null,
    compact = false,
    search = "",
    onNavigate,
  } = $props<{
    course: GuideCourse
    basePath: string
    activeLessonKey?: string | null
    compact?: boolean
    search?: string
    onNavigate?: () => void
  }>()
  const milestoneTitles = [
    "Sammelplanung verstehen",
    "Tagesgeschäft",
    "Rückmeldungen und Briefe",
    "Betrieb einrichten",
  ]
  const progress = $derived(getLearningPathProgress(course.lessons, search))
  const groups = $derived(
    progress.milestones.map((milestone, index) => ({
      ...milestone,
      title: `${index + 1}. ${milestoneTitles[index]}`,
      lessons: milestone.lessonKeys.flatMap((key) => {
        const lesson = course.lessons.find((candidate) => candidate.key === key)

        return lesson ? [lesson] : []
      }),
    })),
  )
  let expanded = $state<Record<string, boolean>>({})
  const navigationContext = $derived(`${basePath}:${search}:${activeLessonKey ?? "overview"}`)
  const defaultGroupKey = $derived(
    groups.find((group) => group.lessons.some((lesson) => lesson.key === activeLessonKey))?.key ??
      progress.nextMilestone?.key,
  )
</script>

<nav aria-label="Lernkapitel" class={{ compact }}>
  {#each groups as group (group.key)}
    {@const stateKey = `${navigationContext}:${group.key}`}
    <LearningDisclosure
      title={group.title}
      open={expanded[stateKey] ?? group.key === defaultGroupKey}
      onOpenChange={(value) => (expanded = { ...expanded, [stateKey]: value })}
    >
      {#snippet metadata()}
          <span class="topic-count"
          >{group.completedCount}/{group.lessons.length}<span class="sr-only"> erledigt</span></span
        >
      {/snippet}
      <ol>
        {#each group.lessons as lesson (lesson.key)}
          {@const number = getLearningLessonNumber(lesson.key)}
          <li>
            <a
              href={getLearningLessonHref(basePath, lesson, search)}
              use:spaLink
              onclick={onNavigate}
              class={{ recommended: !activeLessonKey && lesson.key === progress.nextLesson?.key }}
              aria-label={`Kapitel ${number}: ${lesson.title}${lesson.completed ? ", erledigt" : ""}`}
              aria-current={lesson.key === activeLessonKey ? "page" : undefined}
            >
              <span class="chapter-number" aria-hidden="true">{number}</span>
              <span class="chapter-title"
                ><span class="sr-only">Kapitel {number}: </span>{lesson.title}</span
              >
              {#if lesson.completed}<span class="done"
                  ><Icon path={mdiCheck} size="1.15rem" /><span class="sr-only">Erledigt</span
                  ></span
                >{/if}
            </a>
          </li>
        {/each}
      </ol>
    </LearningDisclosure>
  {/each}
</nav>

<style>
  nav {
    min-width: 0;
    color: inherit;
  }
  .topic-count {
    font-size: 15px;
    font-weight: 400;
    white-space: nowrap;
  }
  ol {
    margin: 0;
    padding: 0 0 0.65rem;
    list-style: none;
  }
  a {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    padding: 0.75rem 0.5rem;
    color: inherit;
    text-decoration: none;
    line-height: 1.45;
  }
  a:hover {
    background: #f2f4f5;
  }
  a[aria-current="page"] {
    background: #f7eeee;
    box-shadow: inset 3px 0 #7c2024;
    font-weight: 600;
  }
  .chapter-number {
    flex: 0 0 1.8rem;
    font-variant-numeric: tabular-nums;
    text-align: center;
    color: #526c77;
  }
  a.recommended {
    background: #f7eeee;
    box-shadow: inset 3px 0 #7c2024;
    font-weight: 600;
  }
  .chapter-title {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .done {
    flex: 0 0 1.2rem;
    color: #246b43;
  }
  a:focus-visible {
    outline: 2px solid #7c2024;
    outline-offset: 2px;
  }
  .compact a {
    font-size: 16px;
    padding-inline: 0.35rem;
  }
  .compact .chapter-number {
    flex-basis: 1.35rem;
  }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
</style>
